/* eslint-disable function-paren-newline */
import { CartItem, CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import {
  isProductUnAvailable,
  numberOfAvailableUnits,
  showAvailabilityWarning,
} from '@helpers/product';
import React from 'react';
import ProductData from '@customTypes/product';
import { isArray, isEmpty } from 'lodash';
import useSoldOut from '@hooks/useSoldOut';

const productTagsClass = {
  IN_CART: 'bg-solar-surface-tertiary text-white',
  UNITS_LEFT: 'bg-solar-surface-tertiary text-white',
  UNAVAILABLE: 'bg-rp-gray-divider text-solar-secondary',
};

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  product: ProductData;
};

function ProductTag({ tagClass, tagContent }: { tagClass: string; tagContent: string }) {
  return (
    <div
      className={`flex font-rp-basetica items-center justify-center p-1 rounded text-xs leading-14px w-fit gap-1 whitespace-nowrap ${tagClass}`}
    >
      {tagContent}
    </div>
  );
}

function ProductTagFactory({ date, cartData, hotelDetails, product }: Props) {
  const { items: cartItems } = cartData || {};
  const { getSoldOutTextForProduct } = useSoldOut();

  const showAvailableUnits =
    date && showAvailabilityWarning(product, cartData) && !product.is_product_time_group;

  let matchingProduct;
  let tagContent = '';
  let tagClass = '';

  // Find the matching product in the cart
  if (product.is_product_time_group) {
    // Extract the product IDs from the children array
    const childProductIds = product.children?.map((childProduct) => childProduct.product_id);

    // Find the products that match any of the child IDs
    matchingProduct = cartItems?.filter((cartItem) =>
      childProductIds?.includes(cartItem.product_id),
    );
  } else if (product.individual_billing) {
    matchingProduct = cartItems?.find((item) => item.product_id === product.product_id);
  } else if (!product.individual_billing) {
    matchingProduct = cartItems?.filter((item) => item.product_id === product.product_id);
  }

  // IN CART tag
  if (cartItems && !isEmpty(matchingProduct)) {
    // Time Slot product
    if (product.is_product_time_group && isArray(matchingProduct) && matchingProduct.length) {
      let guestCount = 0;
      if (product.individual_billing) {
        guestCount = matchingProduct.reduce(
          (sum: number, item: CartItem) => sum + item.no_of_adults + item.no_of_childs,
          0,
        );
      } else {
        guestCount = matchingProduct.length;
      }

      tagContent = `${guestCount} in Cart`;
      tagClass = productTagsClass.IN_CART;

      // Individual billing product
    } else if (product.individual_billing && !isArray(matchingProduct)) {
      const adultAndChildrenTotal = matchingProduct.no_of_adults + matchingProduct.no_of_childs;

      if (adultAndChildrenTotal > 0) {
        tagContent = `${adultAndChildrenTotal} in Cart`;
        tagClass = productTagsClass.IN_CART;
      }
      // Group billing product
    } else if (!product.individual_billing && isArray(matchingProduct)) {
      if (matchingProduct.length > 0) {
        tagContent = `${matchingProduct.length} in Cart`;
        tagClass = productTagsClass.IN_CART;
      }
    }
    // Available Units tag
  } else if (
    showAvailableUnits &&
    date &&
    !isProductUnAvailable(product, cartData, hotelDetails, date)
  ) {
    tagContent = `Only ${numberOfAvailableUnits(product, cartData)} Left`;
    tagClass = productTagsClass.UNITS_LEFT;
  } else if (date && isProductUnAvailable(product, cartData, hotelDetails, date)) {
    tagContent = getSoldOutTextForProduct(product);
    tagClass = productTagsClass.UNAVAILABLE;
  }

  return <ProductTag tagContent={tagContent} tagClass={tagClass} />;
}

export default ProductTagFactory;
